import React, { useState, useEffect } from 'react';
import { 
  Card, 
  List, 
  Avatar, 
  Typography, 
  Tag, 
  Button, 
  Space, 
  Spin, 
  Empty,
  Row,
  Col,
  Divider,
  Table,
  Input,
  Tooltip
} from 'antd';
import { 
  UserOutlined, 
  CalendarOutlined, 
  ClockCircleOutlined, 
  DollarOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined as PendingIcon,
  SearchOutlined,
  ReloadOutlined,
  FileTextOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import ReferenceRequestModal from '../../../components/Common/ReferenceRequestModal';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

const { Title, Text, Paragraph } = Typography;

interface ApprovedApplication {
  id: number;
  name: string;
  type: number;
  mentorId: number;
  menteeId: number;
  eventDate?: string;
}

const ReferenceRequestsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<ApprovedApplication | null>(null);

  useEffect(() => {
    dayjs.locale('tr');
  }, []);

  const { data: applications = [], isLoading: loading, refetch } = useQuery({
    queryKey: mentorProfileQueryKeys.approvedApplications,
    queryFn: async () => {
      const res = await apiService.getMentorApprovedApplications();
      if (!res.success) throw new Error(res.message);
      return res.data ?? [];
    },
  });

  const getTypeText = (type: number) => {
    return type === 0 ? '1-1 Görüşme' : 'Bootcamp';
  };

  const getTypeColor = (type: number) => {
    return type === 0 ? 'blue' : 'green';
  };

  const getTypeIcon = (type: number) => {
    return type === 0 ? <UserOutlined /> : <CalendarOutlined />;
  };

  const handleSendReferenceRequest = (application: ApprovedApplication) => {
    setSelectedApplication(application);
    setModalVisible(true);
  };

  const handleModalSuccess = () => {
    if (selectedApplication) {
      queryClient.setQueryData(
        mentorProfileQueryKeys.approvedApplications,
        (old: ApprovedApplication[] | undefined) =>
          (old ?? []).filter(
            (app) =>
              !(
                app.id === selectedApplication.id &&
                app.type === selectedApplication.type
              )
          )
      );
    }
    setModalVisible(false);
    setSelectedApplication(null);
    queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.approvedApplications });
  };

  const filteredApplications = (applications || []).filter(app =>
    app.name.toLowerCase().includes(searchText.toLowerCase()) ||
    getTypeText(app.type).toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Başvuru',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: ApprovedApplication) => (
        <Space>
          <Avatar 
            size={40} 
            icon={getTypeIcon(record.type)}
            style={{ backgroundColor: getTypeColor(record.type) === 'blue' ? 'var(--primary-500)' : 'var(--success)' }}
          />
          <div>
            <Text strong>{name}</Text>
            <br />
            <Tag 
              color={getTypeColor(record.type)} 
              icon={getTypeIcon(record.type)}
              style={{ fontSize: '12px', marginTop: '4px' }}
            >
              {getTypeText(record.type)}
            </Tag>
          </div>
        </Space>
      ),
    },
    {
      title: 'Tarih',
      dataIndex: 'eventDate',
      key: 'eventDate',
      render: (eventDate: string | undefined) => (
        <Text type="secondary">
          {eventDate ? dayjs(eventDate).format('DD MMM YYYY HH:mm') : '-'}
        </Text>
      ),
    },
    {
      title: 'Mentee ID',
      dataIndex: 'menteeId',
      key: 'menteeId',
      render: (menteeId: number) => (
        <Text type="secondary">#{menteeId}</Text>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: ApprovedApplication) => (
        <Space>
          <Button 
            type="primary" 
            icon={<SendOutlined />}
            onClick={() => handleSendReferenceRequest(record)}
            size="small"
          >
            Referans Talebi Gönder
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input
          placeholder="Başvuru adı veya tip ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => refetch()}
          loading={loading}
        >
          Yenile
        </Button>
      </div>

      {filteredApplications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={searchText ? "Arama kriterlerinize uygun başvuru bulunamadı" : "Henüz onaylanmış başvurunuz bulunmuyor"}
          style={{ marginTop: '50px' }}
        />
      ) : (
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredApplications}
          rowKey={(record) => `${record.type}-${record.id}`}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} başvuru`,
          }}
        />
      )}

      <ReferenceRequestModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleModalSuccess}
        application={selectedApplication}
        userType="mentor"
      />
    </div>
  );
};

export default ReferenceRequestsTab;
